import React from "react";
import { Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Wordmark from "../components/Wordmark";
import { CONTACT_EMAIL, SECTION_IDS } from "../landingConfig";

/**
 * Footer. Deliberately factual: no newsletter signup, and no social icons
 * because there are no verified accounts to link to.
 *
 * The Documents column is where the platform's legal texts are reachable from.
 * Only the Privacy Policy is published so far; the two consent forms are paper
 * documents the school hands to parents and staff.
 *
 * `showSectionNav` exists because the section links are `#anchors` into the
 * landing page. Reused on a standalone page (the legal documents), those
 * anchors point at nothing, so that page passes `false` rather than rendering
 * four links that do nothing.
 */
export default function Footer({ showSectionNav = true }) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const navItems = [
    { href: `#${SECTION_IDS.features}`, label: t("landing.nav.features") },
    { href: `#${SECTION_IDS.how}`, label: t("landing.nav.how") },
    { href: `#${SECTION_IDS.results}`, label: t("landing.nav.results") },
    { href: `#${SECTION_IDS.audiences}`, label: t("landing.nav.audiences") },
  ];

  return (
    <footer className="border-t border-line bg-white px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div
          className={`grid gap-10 md:grid-cols-2 ${
            showSectionNav ? "lg:grid-cols-4" : "lg:grid-cols-3"
          }`}
        >
          <div>
            <Wordmark />
            <p className="mt-4 max-w-xs leading-relaxed text-muted">
              {t("landing.footer.tagline")}
            </p>
          </div>

          {showSectionNav && (
            <nav aria-label={t("landing.footer.nav_title")}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-black-landing">
                {t("landing.footer.nav_title")}
              </h2>
              <ul className="mt-4 flex flex-col gap-1">
                {navItems.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      className="inline-flex min-h-11 cursor-pointer items-center text-muted transition-colors duration-150 ease-out hover:text-black-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <nav aria-label={t("landing.footer.legal_title")}>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-black-landing">
              {t("landing.footer.legal_title")}
            </h2>
            <ul className="mt-4 flex flex-col gap-1">
              <li>
                <Link
                  to="/privacy"
                  className="inline-flex min-h-11 cursor-pointer items-center text-muted transition-colors duration-150 ease-out hover:text-black-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  {t("landing.footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="inline-flex min-h-11 cursor-pointer items-center text-muted transition-colors duration-150 ease-out hover:text-black-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  {t("landing.footer.terms")}
                </Link>
              </li>
            </ul>
          </nav>

          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-black-landing">
              {t("landing.footer.contact_title")}
            </h2>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-4 inline-flex min-h-11 cursor-pointer items-center gap-2 text-muted transition-colors duration-150 ease-out hover:text-black-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
              {CONTACT_EMAIL}
            </a>
            <div className="mt-4">
              <Link
                to="/login"
                className="inline-flex min-h-11 cursor-pointer items-center font-medium text-primary transition-colors duration-150 ease-out hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                {t("landing.nav.login")}
              </Link>
            </div>
            <div className="mt-6">
              <LanguageSwitcher variant="inline" />
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-line pt-6">
          <p className="text-sm text-muted">{t("landing.footer.rights", { year })}</p>
          <p className="text-sm text-muted">{t("landing.footer.disclaimer")}</p>
        </div>
      </div>
    </footer>
  );
}
